import React, { useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Compass, UserCog, ArrowRight, Loader2, BookOpen, Sparkles, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
/**
 * Sprint 1.0c U4/U5 — Onboarding refonte (Jun 3 2026).
 *
 * Replaces the legacy 3-choice screen (Apprendre / Transmettre / Les deux)
 * + 5-slide tutorial. The dual-rôle is native — no mode to activate — so the
 * screen is now a single welcome card with 2 primary CTAs + 1 skip link.
 *
 * Behaviour:
 *   • Shown ONCE after signup. On any CTA click, the backend sets
 *     `onboarding_completed = true` and the user lands on the target page.
 *   • If a user revisits /onboarding after completion, OnboardingRoute in
 *     App.js already redirects them to /app/home.
 *   • Existing users have `onboarding_completed = true` set by the migration
 *     (POST /api/admin/migrations/run-onboarding-flag) so they never see this
 *     screen.
 */
const Onboarding = () => {
  // Note: react-router's navigate() is intentionally unused here — see
  // finishAndGo for the rationale (hard navigation breaks the route-guard
  // race that would otherwise hijack our CTA destinations).
  const { user } = useAuth();
  const [pending, setPending] = useState(null); // 'discover' | 'profile' | 'skip'

  const firstName = (user?.display_name || '').split(' ')[0] || '';

  const finishAndGo = async (target, destination) => {
    setPending(target);
    try {
      // Single backend call, idempotent — flips onboarding_completed=true.
      await axios.post(`${API}/onboarding/complete`, {}, { withCredentials: true });
    } catch (e) {
      // Even if the network call fails we still navigate — the user has clearly
      // engaged. The next /auth/me call will re-evaluate the flag.
      console.error('[onboarding] complete failed:', e);
    }
    // Sprint 1.0c U4/U5 (testing iter 48 fix v2) — hard navigation to break
    // the React-Router race condition.
    //
    // Soft-navigation via navigate() + setUser races against the route guards:
    //   • OnboardingRoute sees user.onboarding_completed flip → fires its own
    //     <Navigate to="/app/home" replace/>.
    //   • ProtectedRoute on the target route sees the stale flag and bounces
    //     back to /onboarding.
    // Either way both non-/app/home CTAs end up on /app/home.
    //
    // A hard reload is acceptable here because (a) it's a one-time post-signup
    // event, (b) there's no React state worth preserving on the welcome
    // screen, and (c) it guarantees /auth/me re-fetches the now-true flag
    // before any route guard runs.
    window.location.assign(destination);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-10"
      style={{ background: 'linear-gradient(135deg, #0d1f35 0%, #0f2540 50%, #0d1f35 100%)' }}
      data-testid="onboarding-welcome"
    >
      <div className="w-full max-w-3xl bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-3xl shadow-2xl px-7 py-10 sm:px-10 sm:py-12 text-center">
        {/* Logo / wordmark */}
        <p
          className="text-2xl font-bold bg-gradient-to-r from-cyan-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-8"
          data-testid="onboarding-logo"
        >
          OpenSapiens
        </p>

        {/* Greeting */}
        <h1 className="text-2xl sm:text-3xl font-medium text-white">
          Bienvenue sur OpenSapiens{firstName ? `, ${firstName}` : ''}&nbsp;!
        </h1>

        {/* Dual-rôle anchor */}
        <p className="text-sm sm:text-base text-gray-300 mt-4 leading-relaxed">
          Ici, tu peux <strong className="text-white">apprendre ET transmettre</strong> depuis le
          même compte — rien à activer, rien d&apos;obligatoire. Tu avances à ton rythme.
        </p>

        {/* Brief 27 oct G.2 — bénéfices concrets + exemples de personas */}
        <div className="mt-7 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left" data-testid="onboarding-benefits">
          {[
            { icon: BookOpen, title: 'Apprends en direct', text: 'Réserve une session 1-to-1 ou un atelier avec un expert — gratuit pendant la beta.', example: 'Comme Alexandre, fondateur, qui a clarifié sa stratégie produit en 45 min.' },
            { icon: Sparkles, title: 'Transmets ce que tu sais', text: 'Ouvre des créneaux quand tu veux : ton calendrier public attire celles et ceux qui ont besoin de toi.', example: 'Comme Sophie, coach, qui a donné 4 sessions dès sa première semaine.' },
            { icon: Users, title: 'Rejoins ta communauté', text: 'Échange, publie, pose tes questions — avec des gens qui partagent tes sujets.', example: 'Comme la communauté Product Managers Suisse et ses 22 membres.' },
          ].map(({ icon: Icon, title, text, example }) => (
            <div key={title} className="rounded-2xl bg-white/[0.04] border border-white/10 p-4" data-testid={`onboarding-benefit-${title.split(' ')[0].toLowerCase()}`}>
              <div className="h-8 w-8 rounded-lg flex items-center justify-center mb-2.5" style={{ backgroundColor: 'rgba(127,119,221,0.2)' }}>
                <Icon className="h-4 w-4" style={{ color: '#BBB3FF' }} />
              </div>
              <p className="text-sm font-semibold text-white">{title}</p>
              <p className="text-xs text-gray-300 mt-1 leading-relaxed">{text}</p>
              <p className="text-[11px] text-gray-400 mt-2 italic leading-snug">{example}</p>
            </div>
          ))}
        </div>

        <p className="text-xs text-gray-400 mt-6">Par où commencer ? Les deux options prennent moins de 2 minutes — et tu peux changer d&apos;avis à tout moment.</p>

        {/* 2 primary CTAs side by side (stack on mobile) */}
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            disabled={!!pending}
            onClick={() => finishAndGo('discover', '/app/discover')}
            data-testid="onboarding-cta-discover"
            className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
            style={{ backgroundColor: '#7F77DD' }}
          >
            {pending === 'discover' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Compass className="h-4 w-4" />}
            Découvrir les experts
            <ArrowRight className="h-4 w-4" />
          </button>
          <button
            type="button"
            disabled={!!pending}
            onClick={() => finishAndGo('profile', `/app/profile/${user?.user_id}?edit=presentation`)}
            data-testid="onboarding-cta-profile"
            className="inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold transition-colors hover:bg-white/5 disabled:opacity-60"
            style={{ borderWidth: '1px', borderStyle: 'solid', borderColor: '#7F77DD', color: '#BBB3FF', backgroundColor: 'transparent' }}
          >
            {pending === 'profile' ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCog className="h-4 w-4" />}
            Compléter mon profil
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>

        {/* Skip link */}
        <button
          type="button"
          disabled={!!pending}
          onClick={() => finishAndGo('skip', '/app/home')}
          data-testid="onboarding-skip"
          className="mt-6 text-xs text-gray-400 hover:text-gray-200 transition-colors disabled:opacity-60 inline-flex items-center gap-1"
        >
          Passer et explorer par moi-même <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
};

export default Onboarding;
